import * as React from "react";
import { observer } from "mobx-react-lite";
import { notification } from "antd";
import { useDetailContext } from "../../components/detail/detailContext";
import { PromptContext } from "../../mobxStores/prompt/prompt";
import { useCleanPrompt } from "../../reactHooks/useCleanPrompt";
import { FormButtons } from "../../components/forms/formButtons";
import { IpSet } from "../../schema/types/ipset";
import { linksOf, request } from "../../services/cpln";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import { NGSelect } from "../../newcomponents/select/ngselect";
import { NGKindSelect } from "../../newcomponents/select/ngkindselect";
import { NGInputListMst } from "../../newcomponents/inputList/inputListMst";
import { ngParseLink } from "../../utils/linkParser/linkParser";
import { Workload } from "../../schema/types/workload/workload";
import clsx from "clsx";
import { isEqual } from "lodash";
import { NGLabel } from "../../newcomponents/text/label";
import { SelectModel } from "../../mobxDataModels/selectModel";
import { NGFormElement } from "../../newcomponents/ngformelement/ngformelement";
import { Gvc } from "../../schema/types/gvc";
import { NGError } from "../../newcomponents/text/error";
import NGAlert from "../../newcomponents/alert";
import { trySaveWithReleaseWarning } from "../../utils/releaseWarning";

interface Props {
  ipset: IpSet;
}
const SpecRaw: React.FC<Props> = ({ ipset }) => {
  function getDefaultTarget() {
    if (!ipset.spec.link) {
      return "workload";
    }
    const { isValid, kind } = ngParseLink(ipset.spec.link, { useInputCtx: true });
    return isValid ? kind : "workload";
  }
  const targetRef = React.useRef(
    SelectModel.create({
      label: "Target",
      options: [
        { label: "Workload", value: "workload" },
        { label: "GVC", value: "gvc" },
      ],
      initialValue: getDefaultTarget(),
    }),
  );

  function getDefaultGvcName() {
    if (!ipset.spec.link) {
      return "";
    }
    const { isValid, name } = ngParseLink(ipset.spec.link, { useInputCtx: true });
    return isValid ? name : ipset.spec.link;
  }
  const [gvcName, setGvcName] = React.useState(getDefaultGvcName());

  function getDefaultWorkloadLink() {
    if (!ipset.spec.link) {
      return "";
    }
    const { isValid, gvcRelative } = ngParseLink(ipset.spec.link, { useInputCtx: true });
    return isValid ? gvcRelative : ipset.spec.link;
  }
  const [workloadLink, setWorkloadLink] = React.useState(getDefaultWorkloadLink());

  function getDefaultLocations() {
    return (ipset.spec.locations || []).map((l) => ({ firstValue: l.name, secondValue: l.retentionPolicy }));
  }
  const locationsRef = React.useRef(ListOfItemsModel.create({ _items: getDefaultLocations() }));

  const [isLoading, setIsLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [promptWhen, setPromptWhen] = React.useState(false);

  React.useEffect(() => {
    PromptContext.setWhen(promptWhen);
  }, [promptWhen]);

  useCleanPrompt();

  const { fetchItem } = useDetailContext();

  function getSpec() {
    const res: any = {
      locations: locationsRef.current.items.map((i) => {
        const { absolute } = ngParseLink(i.firstValue, { kind: "location" });
        return { name: absolute, retentionPolicy: i.secondValue };
      }),
    };
    if (targetRef.current.value === "workload" && workloadLink) {
      res.link = workloadLink;
    } else if (targetRef.current.value === "gvc" && gvcName) {
      const { absolute } = ngParseLink(gvcName, { kind: "gvc" });
      res.link = absolute;
    }
    return res;
  }

  React.useEffect(() => {
    for (const item of locationsRef.current.items) {
      if (!item.secondValue) {
        item.setSecondValue("keep");
      }
    }
  }, [locationsRef.current.items.length]);

  React.useEffect(() => {
    let res = false;
    if (targetRef.current.isDirty) res = true;
    if (targetRef.current.value === "workload") {
      if (workloadLink !== getDefaultWorkloadLink()) res = true;
    } else if (targetRef.current.value === "gvc") {
      if (gvcName !== getDefaultGvcName()) res = true;
    }
    if (
      !isEqual(
        getDefaultLocations(),
        locationsRef.current.items.map((i) => ({ firstValue: i.firstValue, secondValue: i.secondValue })),
      )
    )
      res = true;
    setIsDirty(res);
  }, [ipset.version, workloadLink, gvcName, targetRef.current.value, locationsRef.current.isDirty]);

  React.useEffect(() => {
    setPromptWhen(isDirty);
  }, [isDirty]);

  function handleReset() {
    targetRef.current.reset();
    setWorkloadLink(getDefaultWorkloadLink());
    setGvcName(getDefaultGvcName());
    locationsRef.current.reset();
  }

  function tryHandleSave() {
    trySaveWithReleaseWarning(ipset, handleSave);
  }

  async function handleSave() {
    try {
      setIsLoading(true);

      await request({ method: "patch", body: { "$replace/spec": getSpec() }, url: linksOf(ipset).self! });

      if (fetchItem) {
        await fetchItem();
      }

      notification.success({
        message: "Success",
        description: "Updated ip set",
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      if (e.response.status === 409) {
        if (fetchItem) {
          await fetchItem();
          notification.info({
            message: "Updated Item",
            description: "Fetched the latest version of the item and discarded changes.",
          });
        }
      }
    }
  }

  return (
    <>
      <div>
        <NGFormElement
          label="Target"
          name="target"
          as={"select"}
          style={{ width: 650 }}
          options={targetRef.current.options}
          onChange={(value) => targetRef.current.setValue(value)}
          value={targetRef.current.value}
        />
        <NGLabel>{targetRef.current.value === "workload" ? "Workload" : "GVC"}</NGLabel>
        {targetRef.current.value === "workload" ? (
          <>
            <NGKindSelect<Workload>
              kind="workload"
              className={!!workloadLink && !workloadLink.startsWith("//gvc/") ? "mb-1" : "mb-4"}
              style={{ width: 650 }}
              renderOption={({ getOptionValue, getOptionLabel, option, isHovered, isSelected, props }) => {
                const { isValid, name: gvcName } = ngParseLink(linksOf(option).gvc || "", { useInputCtx: true });

                return (
                  <li
                    key={getOptionValue(option)}
                    {...props}
                    className={clsx(`flex items-center justify-between`, {
                      "option-hover": isHovered,
                      "option-selected": isSelected,
                    })}
                  >
                    <span>{getOptionLabel(option)}</span>
                    {isValid ? <span>{gvcName}</span> : null}
                  </li>
                );
              }}
              getOptionValue={(option) => {
                const link = linksOf(option).self;
                if (!link) {
                  return `invalid item - ${option.name}`;
                }

                const { isValid, gvcRelative } = ngParseLink(link, { useInputCtx: true });
                if (!isValid) {
                  return `invalid item - ${option.name}`;
                }
                return gvcRelative;
              }}
              invalid={!!workloadLink && !workloadLink.startsWith("//gvc/")}
              value={workloadLink}
              onChange={(value) => setWorkloadLink(value)}
            />
            {!!workloadLink && !workloadLink.startsWith("//gvc/") ? (
              <NGError className="mb-4">
                Workload link must be formatted as "//gvc/gvcName/workload/workloadName" to specify gvc of the workload.
              </NGError>
            ) : !!workloadLink && (getDefaultWorkloadLink() !== workloadLink || targetRef.current.isDirty) ? (
              <NGAlert
                className="mb-4"
                type={"warning"}
                title={`Changing the ip set will cause a temporary outage for all endpoints in this Workload.`}
              />
            ) : null}
          </>
        ) : null}
        {targetRef.current.value === "gvc" ? (
          <>
            <NGKindSelect<Gvc>
              kind="gvc"
              className="mb-4"
              style={{ width: 650 }}
              optionValueAs={"name"}
              value={gvcName}
              onChange={(value) => setGvcName(value)}
            />
            {!!gvcName && (getDefaultGvcName() !== gvcName || targetRef.current.isDirty) ? (
              <NGAlert
                className="mb-4"
                type={"warning"}
                title={`Changing the ip set will cause a temporary outage for all endpoints in this GVC.`}
              />
            ) : null}
          </>
        ) : null}
        <NGInputListMst
          label="Locations"
          styles={{
            header: { width: 650 },
            container: { width: 650 },
            secondInputContainer: { minWidth: 100, flexGrow: 0 },
          }}
          data={locationsRef.current}
          firstLabel="Location"
          firstInput={(i) => (
            <NGKindSelect kind="location" value={i.firstValue} onChange={(value) => i.setFirstValue(value)} />
          )}
          secondLabel="Retention"
          secondInput={(i) => (
            <NGSelect
              options={[
                { label: "Free", value: "free" },
                { label: "Keep", value: "keep" },
              ]}
              value={i.secondValue}
              onChange={(value) => i.setSecondValue(value)}
            />
          )}
        />
      </div>
      <div style={{ width: 450 }} className="mt-8 flex items-center">
        <FormButtons
          onReset={handleReset}
          onSave={tryHandleSave}
          resetDisabled={isLoading || !isDirty}
          saveDisabled={isLoading || !isDirty}
          loading={isLoading}
        />
      </div>
    </>
  );
};

export const Spec = observer(SpecRaw);
